import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Build as BuildIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { fixPartsCostData, validatePartsCostData } from '../../utils/fixPartsCostData';

const PartsCostFixPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      const results = await validatePartsCostData();
      setValidationResults(results);
    } catch (err: any) {
      setError(err.message || 'حدث خطأ أثناء التحقق من البيانات');
    } finally {
      setValidating(false);
    }
  };

  const handleFix = async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await fixPartsCostData();
      setResults(results);
    } catch (err: any) {
      setError(err.message || 'حدث خطأ أثناء إصلاح البيانات');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        إصلاح تكلفة قطع الغيار
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        هذه الأداة تقوم بإصلاح مشكلة تكلفة قطع الغيار في التقارير وتوحيد استخدام الأسعار في النظام.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {/* بطاقة التحقق */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <CheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              التحقق من البيانات
            </Typography>
            <Typography variant="body2" color="text.secondary">
              فحص السجلات الحالية للتأكد من صحة بيانات قطع الغيار
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="outlined"
              onClick={handleValidate}
              disabled={validating}
              startIcon={validating ? <CircularProgress size={20} /> : <PlayIcon />}
            >
              {validating ? 'جاري التحقق...' : 'بدء التحقق'}
            </Button>
          </CardActions>
        </Card>

        {/* بطاقة الإصلاح */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              إصلاح البيانات
            </Typography>
            <Typography variant="body2" color="text.secondary">
              تطبيق الإصلاحات على جميع السجلات وتوحيد الحسابات
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              color="primary"
              onClick={handleFix}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <BuildIcon />}
            >
              {loading ? 'جاري الإصلاح...' : 'بدء الإصلاح'}
            </Button>
          </CardActions>
        </Card>
      </Box>

      {/* نتائج التحقق */}
      {validationResults && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <CheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            نتائج التحقق
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Alert severity="success" sx={{ flex: 1 }}>
              سجلات صحيحة: {validationResults.valid}
            </Alert>
            <Alert severity={validationResults.invalid > 0 ? 'warning' : 'info'} sx={{ flex: 1 }}>
              سجلات تحتاج إصلاح: {validationResults.invalid}
            </Alert>
          </Box>

          {validationResults.issues && validationResults.issues.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                المشاكل المكتشفة:
              </Typography>
              <List dense>
                {validationResults.issues.slice(0, 10).map((issue: string, index: number) => (
                  <ListItem key={index}>
                    <ListItemText primary={issue} />
                  </ListItem>
                ))}
                {validationResults.issues.length > 10 && (
                  <ListItem>
                    <ListItemText 
                      primary={`... و ${validationResults.issues.length - 10} مشاكل أخرى`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </Paper>
      )}

      {/* نتائج الإصلاح */}
      {results && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            نتائج الإصلاح
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Alert severity="info" sx={{ flex: 1 }}>
              إجمالي السجلات: {results.total}
            </Alert>
            <Alert severity="success" sx={{ flex: 1 }}>
              تم التحديث: {results.updated}
            </Alert>
            <Alert severity={results.errors > 0 ? 'error' : 'success'} sx={{ flex: 1 }}>
              أخطاء: {results.errors}
            </Alert>
          </Box>

          {results.updated > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              ✅ تم إصلاح {results.updated} سجل بنجاح! يمكنك الآن مراجعة التقارير للتأكد من صحة البيانات.
            </Alert>
          )}

          {results.errors > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              ⚠️ حدثت {results.errors} أخطاء أثناء الإصلاح. راجع وحدة التحكم للمزيد من التفاصيل.
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          ملاحظات مهمة:
        </Typography>
        <Typography variant="body2" component="div">
          <ul>
            <li>تأكد من عمل نسخة احتياطية من قاعدة البيانات قبل تشغيل الإصلاح</li>
            <li>قد تستغرق العملية بعض الوقت حسب عدد السجلات</li>
            <li>ينصح بتشغيل التحقق أولاً لمعرفة حالة البيانات</li>
            <li>تأكد من عدم وجود مستخدمين آخرين يعملون على النظام أثناء الإصلاح</li>
          </ul>
        </Typography>
      </Paper>
    </Box>
  );
};

export default PartsCostFixPage;